import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import Toast, { type ToastLink, type ToastType } from '../components/Toast';

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
  link?: ToastLink;
  duration: number;
}

interface AddToastOptions {
  link?: ToastLink;
  duration?: number;
}

interface ToastContextType {
  addToast: (message: string, type?: ToastType, options?: AddToastOptions) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

const DEFAULT_DURATION_MS = 5000;

/**
 * App-level toast host. Toast state lives above the router so toasts survive route
 * changes (e.g. ManualUpload remounts when navigating from /upload to /processing/:id).
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(0);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((message: string, type: ToastType = 'success', options?: AddToastOptions) => {
    const id = nextId.current++;
    const duration = options?.duration ?? DEFAULT_DURATION_MS;
    setToasts((prev) => [...prev, { id, message, type, link: options?.link, duration }]);
  }, []);

  const value = useMemo(() => ({ addToast }), [addToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end space-y-2 pointer-events-none">
        {toasts.map((t) => (
          <ToastEntry key={t.id} toast={t} onRemove={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// Keeps `onClose` referentially stable per toast so Toast's auto-dismiss timer isn't reset on re-render.
function ToastEntry({ toast, onRemove }: { toast: ToastItem; onRemove: (id: number) => void }) {
  const handleClose = useCallback(() => onRemove(toast.id), [onRemove, toast.id]);
  return (
    <Toast
      isVisible
      message={toast.message}
      type={toast.type}
      link={toast.link}
      duration={toast.duration}
      onClose={handleClose}
    />
  );
}

export function useToast(): ToastContextType {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
